import cx from 'classnames'
import Image from 'next/image'

import { addComma } from '@lib/utils'
import { ChallengeUnit, ChallengeUnitValue } from '@interfaces/UserMissionEnum'
import { Crown2Icon } from '@components/icons/Crown2Icon'
import { TrophyIcon } from '@components/icons/TrophyIcon'
import { CustomLinkProps, UserMissionCardProps } from './interface'

function CustomLink({ children, missionLink }: CustomLinkProps) {
  if (missionLink)
    return (
      <a href={missionLink} target='_blank' rel='noreferrer'>
        {children}
      </a>
    )

  return children
}

export function UserMissionCard({
  data,
  trophy,
  showNextLevel,
}: UserMissionCardProps) {
  const isMaxLevel = data.currentLevel === data.maxLevel

  return (
    <div className='w-[375px] tablet-mobile:w-full  border border-gray-4 dark:border-purple rounded-lg shadow p-[20px] flex flex-col items-center space-y-[20px]'>
      <p className='font-mitr font-medium text-secondary dark:text-dark-10 text-center'>
        {data.title}
      </p>

      <div className='grid justify-items-center items-center justify-center gap-y-[10px]'>
        <CustomLink missionLink={data.missionLink}>
          <div
            className={cx(
              'flex items-center justify-center w-[100px] h-[100px] rounded-full overflow-hidden bg-blue-2',
              {
                'opacity-20': !data.completed && data.currentLevel === 1,
              }
            )}
          >
            {data.iconUrl ? (
              <div className='relative w-full h-full pt-[100%]'>
                <Image src={data.iconUrl} alt='user-mission' layout='fill' />
              </div>
            ) : (
              <Crown2Icon width='100%' height='100%' />
            )}
          </div>
        </CustomLink>

        <div className='grid gap-y-[9px]'>
          <div className='text-center'>
            <p
              className={cx('text-primary font-bold text-[14px] mb-[4px]', {
                'opacity-20 dark:opacity-40':
                  !data.completed && data.currentLevel === 1,
              })}
            >
              Lv. {data.currentLevel} {data.currentLevelName}
            </p>
            <p className='font-light text-[12px] text-secondary-1 text-center'>
              {data.criteria}
              {!isMaxLevel
                ? ` ${addComma(data.previousTarget || 0)} - ${addComma(
                    data.nextTarget - 1 || 0
                  )} `
                : ` ${addComma(data.previousTarget || 0)} `}
              {ChallengeUnitValue[ChallengeUnit[data.unit]]}
              {isMaxLevel && 'ขึ้นไป'}
            </p>
          </div>
          <div className='text-xs tablet:text-[11px] font-light text-secondary-1 text-center h-[37px]'>
            <p>
              {data.criteria}อีก
              <span className='text-primary font-bold'>
                {!data.completed && data.currentLevel === 1
                  ? ` 0 / 1 `
                  : isMaxLevel
                  ? ` ${addComma(data.completed)} `
                  : ` ${addComma(
                      data.nextTarget - data.nextRemaining || 0
                    )} / ${addComma(data.nextTarget || 0)} `}
              </span>
              {ChallengeUnitValue[ChallengeUnit[data.unit]]}
              {data.maxLevel - 1 === data.currentLevel && <span>ขึ้นไป</span>}
            </p>
            {showNextLevel && (
              <p className='mt-[5px]'>
                เพื่อเลื่อนระดับเป็น{' '}
                <span className='text-primary font-bold'>
                  {!data.completed && data.currentLevel === 1 ? (
                    <>
                      Lv. {data.currentLevel} {data.currentLevelName}
                    </>
                  ) : (
                    <>
                      Lv. {data.nextLevel} {data.nextLevelName}
                    </>
                  )}
                </span>
              </p>
            )}
          </div>
        </div>
      </div>

      <div
        className='grid gap-x-[2px] min-h-[62px]'
        style={{
          gridTemplateColumns: `repeat(${
            trophy.length < 10 ? trophy.length : 10
          }, minmax(0, 1fr))`,
        }}
      >
        {trophy.map((r, i) => (
          <div
            key={i}
            className={cx(
              'w-[30px] h-[30px] rounded-full overflow-hidden bg-blue-2 flex items-center justify-center',
              {
                'opacity-30': !r.active,
              }
            )}
          >
            {r.iconUrl ? (
              <div className='relative w-full h-full pt-[100%]'>
                <Image src={r.iconUrl} alt='challengeUrl' layout='fill' />
              </div>
            ) : (
              <TrophyIcon />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
